import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/features/auth/auth.service';
import { MessagesApiService } from 'src/app/features/chat/services/messagesApi.service';
import { buildStorySetupMessageWithTranslate } from 'src/app/features/chat/components/story-setup-wizard/build-story-setup-message-with-translate';
import {
  STORY_SETUP_MEANING,
  type StorySetupAnswers,
} from 'src/app/features/chat/components/story-setup-wizard/story-setup.types';
import { toSetupPreferencesJson } from 'src/app/features/chat/utils/setup-preferences';
import { StoriesService } from './stories.service';
import { SupabaseService } from '../supabase/supabase.service';
import { PosthogService } from '../analytics/posthog.service';
import { randomId } from 'src/app/features/chat/utils/randomId';
import type { Json } from '../supabase/types/database.types';
import { DEFAULT_GAME_SYSTEM_ID } from '../game-systems/game-system-id';

const SETTING_POLL_INTERVAL_MS = 2500;
const SETTING_POLL_TIMEOUT_MS = 180_000;

export type StoryAiSetupContext = {
  storyId: number;
  storyChatId: number;
  ownerNames: Map<string, string>;
};

export type CreateAiStoryResult = {
  storyId: number;
  storyChatId: number;
  loreChatId: number;
};

export class StoryAiSetupPersistError extends Error {
  constructor(
    message: string,
    readonly storyId?: number,
  ) {
    super(message);
    this.name = 'StoryAiSetupPersistError';
  }
}

@Injectable({ providedIn: 'root' })
export class StoryAiSetupService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private auth = inject(AuthService);
  private messagesApi = inject(MessagesApiService);
  private storiesService = inject(StoriesService);
  private supabase = inject(SupabaseService);
  private posthog = inject(PosthogService);

  async createAiStory(): Promise<CreateAiStoryResult> {
    const placeholder = this.translate.instant(
      'stories.create.placeholderName',
    );
    this.posthog.action('story_create', 'try', { aiMode: 'AI' });

    const { data, error } = await this.supabase.client.rpc('create_ai_story', {
      p_name: placeholder,
      p_game_system: DEFAULT_GAME_SYSTEM_ID,
    });

    if (error || !data || typeof data !== 'object') {
      this.posthog.action('story_create', 'error', {
        aiMode: 'AI',
        message: error?.message,
      });
      throw error ?? new Error('Creazione campagna AI fallita');
    }

    const payload = data as {
      story_id?: number;
      story_chat_id?: number;
      lore_chat_id?: number;
    };
    const storyId = payload.story_id;
    const storyChatId = payload.story_chat_id;
    const loreChatId = payload.lore_chat_id;

    if (storyId == null || storyChatId == null || loreChatId == null) {
      this.posthog.action('story_create', 'error', {
        aiMode: 'AI',
        message: 'Risposta RPC incompleta',
      });
      throw new Error('Risposta creazione campagna incompleta');
    }

    await firstValueFrom(this.storiesService.getStories());
    this.posthog.action('story_create', 'success', {
      aiMode: 'AI',
      storyId,
    });

    return { storyId, storyChatId, loreChatId };
  }

  async createAiStoryWithPreferences(
    answers: StorySetupAnswers,
    ownerNames: Map<string, string>,
  ): Promise<CreateAiStoryResult> {
    const created = await this.createAiStory();
    try {
      await this.persistPreferences(
        {
          storyId: created.storyId,
          storyChatId: created.storyChatId,
          ownerNames,
        },
        answers,
      );
      return created;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Salvataggio preferenze fallito';
      throw new StoryAiSetupPersistError(message, created.storyId);
    }
  }

  async persistPreferences(
    ctx: StoryAiSetupContext,
    answers: StorySetupAnswers,
  ): Promise<void> {
    const { storyId, storyChatId, ownerNames } = ctx;
    const userId = this.auth.profileSignal()?.id;
    if (!userId) throw new Error('Utente non autenticato');

    this.posthog.action('story_setup_preferences', 'try', {
      storyId,
      chatId: storyChatId,
    });

    const payload = toSetupPreferencesJson(answers);
    await this.storiesService.updateSetupPreferences(storyId, payload);

    const { data: existingSetup } = await this.supabase.client
      .from('Messages')
      .select('id')
      .eq('chatId', storyChatId)
      .contains('meanings', [STORY_SETUP_MEANING])
      .limit(1)
      .maybeSingle();

    if (!existingSetup) {
      const { text } = buildStorySetupMessageWithTranslate(
        payload,
        [],
        ownerNames,
        this.translate,
        { includeCharacters: false },
      );

      const date = new Date().toISOString();
      const localId = randomId();
      const response = await firstValueFrom(
        this.messagesApi.sendMessage({
          chatId: storyChatId,
          userId,
          localId,
          createdAt: date,
          updatedAt: date,
          text,
          image: null,
          meanings: [STORY_SETUP_MEANING],
          messageQuoteId: null,
          impersonate: null,
          extra_data: { storySetup: payload } as Json,
        }),
      );

      if (response.error || !response.data?.[0]?.id) {
        this.posthog.action('story_setup_preferences', 'error', {
          storyId,
          chatId: storyChatId,
        });
        throw new Error(response.error?.message ?? 'Invio preferenze fallito');
      }
    }

    this.posthog.action('story_setup_preferences', 'success', {
      storyId,
      chatId: storyChatId,
    });

    await firstValueFrom(this.storiesService.getStories());
  }

  async commitPreferencesAndGenerateSetting(
    ctx: StoryAiSetupContext,
    answers: StorySetupAnswers,
  ): Promise<void> {
    await this.persistPreferences(ctx, answers);
    await this.callStorySetting(ctx.storyId, ctx.storyChatId);
    await this.waitForStorySetting(ctx.storyId, ctx.storyChatId);
  }

  async isStorySettingInProgress(storyChatId: number): Promise<boolean> {
    const { data, error } = await this.supabase.client
      .from('Chats')
      .select('ai_status')
      .eq('id', storyChatId)
      .maybeSingle();
    if (error) throw error;
    return data?.ai_status != null;
  }

  async retryGenerateSetting(storyId: number, storyChatId: number): Promise<void> {
    await this.callStorySetting(storyId, storyChatId);
    await this.waitForStorySetting(storyId, storyChatId);
  }

  async waitForStorySetting(storyId: number, storyChatId: number): Promise<void> {
    const started = Date.now();
    let sawAiBusy = false;

    while (Date.now() - started < SETTING_POLL_TIMEOUT_MS) {
      const { data, error } = await this.supabase.client
        .from('Stories')
        .select('setting_brief')
        .eq('id', storyId)
        .maybeSingle();

      if (error) throw error;

      const brief = data?.setting_brief?.trim();
      if (brief) {
        await firstValueFrom(this.storiesService.getStories());
        return;
      }

      const { data: chat, error: chatError } = await this.supabase.client
        .from('Chats')
        .select('ai_status')
        .eq('id', storyChatId)
        .maybeSingle();
      if (chatError) throw chatError;

      const aiStatus = chat?.ai_status ?? null;
      if (aiStatus != null) sawAiBusy = true;
      if (sawAiBusy && aiStatus == null) {
        throw new Error('Generazione ambientazione non riuscita');
      }

      await new Promise((resolve) => setTimeout(resolve, SETTING_POLL_INTERVAL_MS));
    }

    throw new Error('Timeout generazione ambientazione');
  }

  private async callStorySetting(storyId: number, storyChatId: number): Promise<void> {
    this.posthog.action('story_setup_setting', 'try', {
      storyId,
      chatId: storyChatId,
    });

    try {
      await firstValueFrom(
        this.http.post(
          `${environment.masterServerUrl}/api/${storyId}/${storyChatId}/story-setting`,
          {},
        ),
      );
    } catch (err) {
      const httpErr = err as HttpErrorResponse;
      if (
        httpErr.status === 409 &&
        httpErr.error?.code === 'STORY_SETTING_ALREADY_SET'
      ) {
        await firstValueFrom(this.storiesService.getStories());
        return;
      }
      throw err;
    }
  }
}
